import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaUserPlus, FaSearch, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const MatchingManagement = () => {
    const [matchings, setMatchings] = useState([]);
    const [users, setUsers] = useState([]);
    const [filteredParents, setFilteredParents] = useState([]);
    const [filteredChildren, setFilteredChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingMatching, setEditingMatching] = useState(null);
    const [formData, setFormData] = useState({
        parent_id: '',
        user_id: '',
        status: 'Pending',
        description: ''
    });
    const [parentSearch, setParentSearch] = useState('');
    const [childSearch, setChildSearch] = useState('');
    const [filterParent, setFilterParent] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSearch, setTempSearch] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);
    const [totalItems, setTotalItems] = useState(0);

    // Status options with proper labels
    const statusOptions = [
        { value: 'Approached', label: 'Approached', color: 'bg-blue-100 text-blue-800' },
        { value: 'Rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
        { value: 'Hold', label: 'Hold', color: 'bg-yellow-100 text-yellow-800' },
        { value: 'Accepted', label: 'Accepted', color: 'bg-green-100 text-green-800' },
        { value: 'Completed', label: 'Completed', color: 'bg-purple-100 text-purple-800' },
        { value: 'Pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' }
    ];

    // Status mapping for display
    const getStatusLabel = (statusValue) => {
        const status = statusOptions.find(s => s.value === statusValue);
        return status ? status.label : statusValue;
    };

    useEffect(() => {
        fetchUsers();
        fetchMatchings();
    }, []);

    useEffect(() => {
        fetchMatchings();
    }, [filterParent, filterStatus, searchTerm]);

    // Filter parents based on search
    useEffect(() => {
        if (parentSearch) {
            setFilteredParents(
                users.filter(user => 
                    user.name.toLowerCase().includes(parentSearch.toLowerCase()) ||
                    user.id.toString().includes(parentSearch)
                )
            );
        } else {
            setFilteredParents(users);
        }
    }, [parentSearch, users]);

    // Filter children based on search and selected parent
    useEffect(() => {
        let filtered = users;
        if (formData.parent_id) {
            filtered = users.filter(user => user.id !== parseInt(formData.parent_id));
        }
        if (childSearch) {
            filtered = filtered.filter(user => 
                user.name.toLowerCase().includes(childSearch.toLowerCase()) ||
                user.id.toString().includes(childSearch)
            );
        }
        setFilteredChildren(filtered);
    }, [childSearch, users, formData.parent_id]);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get('https://app.bihebari.com.np/api/admin/matching/users', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setUsers(response.data.data);
                setFilteredParents(response.data.data);
                setFilteredChildren(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Error loading users');
        }
    };

    const fetchMatchings = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            let url = 'https://app.bihebari.com.np/api/admin/matching?';
            const params = [];
            
            if (filterParent) {
                params.push(`parent_id=${filterParent}`);
            }
            
            const response = await axios.get(url + params.join('&'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                let filteredData = response.data.data;
                
                // Filter by status
                if (filterStatus) {
                    filteredData = filteredData.filter(item => item.status === filterStatus);
                }
                
                // Filter by search term (parent name or user name)
                if (searchTerm) {
                    const searchLower = searchTerm.toLowerCase();
                    filteredData = filteredData.filter(item => 
                        (item.parent_name && item.parent_name.toLowerCase().includes(searchLower)) ||
                        (item.user_name && item.user_name.toLowerCase().includes(searchLower)) ||
                        (item.parent_id && item.parent_id.toString().includes(searchLower)) ||
                        (item.user_id && item.user_id.toString().includes(searchLower))
                    );
                }
                
                setMatchings(filteredData);
                setTotalItems(filteredData.length);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error fetching matchings:', error);
            toast.error('Error loading matching records');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setSearchTerm(tempSearch);
        setCurrentPage(1);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const clearSearch = () => {
        setTempSearch('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Get current page data
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentMatchings = matchings.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(matchings.length / itemsPerPage);

    // Change page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goToPage = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleOpenModal = (matching = null) => {
        if (matching) {
            setEditingMatching(matching);
            setFormData({
                parent_id: matching.parent_id,
                user_id: matching.user_id,
                status: matching.status || 'Pending',
                description: matching.description || ''
            });
        } else {
            setEditingMatching(null);
            setFormData({
                parent_id: '',
                user_id: '',
                status: 'Pending',
                description: ''
            });
        }
        setParentSearch('');
        setChildSearch('');
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingMatching(null);
        setFormData({
            parent_id: '',
            user_id: '',
            status: 'Pending',
            description: ''
        });
        setParentSearch('');
        setChildSearch('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (name === 'parent_id') {
            setChildSearch('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.parent_id || !formData.user_id) {
            toast.error('Please select both parent and user');
            return;
        }
        
        if (formData.parent_id === formData.user_id) {
            toast.error('Parent and user cannot be the same');
            return;
        }
        
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingMatching 
                ? `https://app.bihebari.com.np/api/admin/matching/${editingMatching.id}`
                : 'https://app.bihebari.com.np/api/admin/matching';
            const method = editingMatching ? 'put' : 'post';
            
            const submitData = editingMatching 
                ? { description: formData.description, status: formData.status }
                : formData;
            
            await axios[method](url, submitData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(editingMatching ? 'Matching updated successfully' : 'Matching created successfully');
            handleCloseModal();
            fetchMatchings();
        } catch (error) {
            console.error('Error saving matching:', error);
            toast.error(error.response?.data?.message || 'Error saving matching');
        } finally {
            setLoading(false);
        }
    };

const handleStatusChange = async (id, currentStatus, parentName, userName) => {
    // Define status flow
    const statusFlow = {
        'Pending': 'Approached',
        'Approached': 'Accepted',
        'Accepted': 'Completed',
        'Hold': 'Pending',
        'Rejected': 'Pending'
    };
    
    const newStatus = statusFlow[currentStatus] || 'Pending';
    
    if (window.confirm(`Change status from "${getStatusLabel(currentStatus)}" to "${getStatusLabel(newStatus)}" for matching between "${parentName}" and "${userName}"?`)) {
        try {
            const token = localStorage.getItem('adminToken');
            // FIX: Send only the status, not description with currentStatus
            await axios.put(`https://app.bihebari.com.np/api/admin/matching/${id}`, 
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
            fetchMatchings();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.message || 'Error updating status');
        }
    }
};

    const handleDelete = async (id, parentName, userName) => {
        if (window.confirm(`Are you sure you want to delete matching between "${parentName}" and "${userName}"?`)) {
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`https://app.bihebari.com.np/api/admin/matching/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Matching deleted successfully');
                fetchMatchings();
            } catch (error) {
                console.error('Error deleting matching:', error);
                toast.error('Error deleting matching');
            }
        }
    };

    const getParentName = (parentId) => {
        const user = users.find(u => u.id === parentId);
        return user ? user.name : `User #${parentId}`;
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? user.name : `User #${userId}`;
    };

    const getStatusBadge = (status) => {
        const statusOption = statusOptions.find(s => s.value === status);
        const colorClass = statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
        const label = statusOption ? statusOption.label : status;
        return (
            <span className={`px-2 py-1 text-xs rounded-full ${colorClass}`}>
                {label}
            </span>
        );
    };

    // Generate page numbers
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }
        return pageNumbers;
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Matching Management</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition"
                >
                    <FaUserPlus />
                    <span>Add New Match</span>
                </button>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={tempSearch}
                                onChange={(e) => setTempSearch(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Search by parent or user name..."
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                                onClick={handleSearch}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                            >
                                <FaSearch />
                            </button>
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {/* Filter by Parent */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Parent</label>
                        <select
                            value={filterParent}
                            onChange={(e) => setFilterParent(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">All Parents</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>{user.name} (ID: {user.id})</option>
                            ))}
                        </select>
                    </div>
                    
                    {/* Filter by Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">All Status</option>
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                
                {/* Active Filters */}
                {(filterParent || filterStatus || searchTerm) && (
                    <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600">Active filters:</span>
                        {filterParent && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                Parent: {getParentName(filterParent)}
                                <button onClick={() => setFilterParent('')} className="hover:text-blue-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {filterStatus && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                Status: {getStatusLabel(filterStatus)}
                                <button onClick={() => setFilterStatus('')} className="hover:text-green-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {searchTerm && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                Search: {searchTerm}
                                <button onClick={clearSearch} className="hover:text-purple-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        <button 
                            onClick={() => {
                                setFilterParent('');
                                setFilterStatus('');
                                clearSearch();
                            }} 
                            className="text-red-500 hover:text-red-700 text-sm"
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Results Info */}
            <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                    Showing {currentMatchings.length} of {totalItems} records
                </p>
                <p className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                </p>
            </div>

            {/* Matchings Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <FaSpinner className="text-4xl text-indigo-600 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : currentMatchings.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        No matching records found
                                    </td>
                                </tr>
                            ) : (
                                currentMatchings.map((matching) => (
                                    <tr key={matching.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">{matching.id}</td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {matching.parent_name || getParentName(matching.parent_id)}
                                                </div>
                                                <div className="text-xs text-gray-500">ID: {matching.parent_id}</div>
                                                {matching.parent_email && (
                                                    <div className="text-xs text-gray-400">{matching.parent_email}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {matching.user_name || getUserName(matching.user_id)}
                                                </div>
                                                <div className="text-xs text-gray-500">ID: {matching.user_id}</div>
                                                {matching.user_email && (
                                                    <div className="text-xs text-gray-400">{matching.user_email}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleStatusChange(matching.id, matching.status, matching.parent_name || getParentName(matching.parent_id), matching.user_name || getUserName(matching.user_id))}
                                                className="cursor-pointer hover:opacity-80 transition"
                                                title="Click to change status"
                                            >
                                                {getStatusBadge(matching.status)}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                                            <div className="truncate" title={matching.description || '-'}>
                                                {matching.description || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {new Date(matching.created).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleOpenModal(matching)}
                                                    className="text-blue-600 hover:text-blue-800 transition p-1"
                                                    title="Edit"
                                                >
                                                    <FaEdit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(matching.id, matching.parent_name || getParentName(matching.parent_id), matching.user_name || getUserName(matching.user_id))}
                                                    className="text-red-600 hover:text-red-800 transition p-1"
                                                    title="Delete"
                                                >
                                                    <FaTrash size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition flex items-center gap-2"
                    >
                        <FaChevronLeft />
                        Previous
                    </button>
                    
                    <div className="flex space-x-1">
                        {getPageNumbers().map(pageNum => (
                            <button
                                key={pageNum}
                                onClick={() => goToPage(pageNum)}
                                className={`px-3 py-2 rounded-lg transition ${
                                    currentPage === pageNum
                                        ? 'bg-indigo-600 text-white'
                                        : 'border hover:bg-gray-50'
                                }`}
                            >
                                {pageNum}
                            </button>
                        ))}
                    </div>
                    
                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition flex items-center gap-2"
                    >
                        Next
                        <FaChevronRight />
                    </button>
                </div>
            )}

            {/* Add/Edit Modal with Searchable Dropdowns */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingMatching ? 'Edit Matching' : 'Add New Matching'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                {!editingMatching && (
                                    <>
                                        {/* Parent User with Search */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Parent User <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Search parent by name or ID..."
                                                value={parentSearch}
                                                onChange={(e) => setParentSearch(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mb-2"
                                            />
                                            <select
                                                name="parent_id"
                                                value={formData.parent_id}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                required
                                                size="5"
                                            >
                                                <option value="">Select Parent User</option>
                                                {filteredParents.map(user => (
                                                    <option key={user.id} value={user.id}>
                                                        {user.name} (ID: {user.id}) {user.email ? `- ${user.email}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {filteredParents.length === 0 && (
                                                <p className="text-xs text-red-500 mt-1">No matching users found</p>
                                            )}
                                        </div>
                                        
                                        {/* Child User with Search */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Child User <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Search child by name or ID..."
                                                value={childSearch}
                                                onChange={(e) => setChildSearch(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mb-2"
                                            />
                                            <select
                                                name="user_id"
                                                value={formData.user_id}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                required
                                                size="5"
                                                disabled={!formData.parent_id}
                                            >
                                                <option value="">Select Child User</option>
                                                {filteredChildren.map(user => (
                                                    <option key={user.id} value={user.id}>
                                                        {user.name} (ID: {user.id}) {user.email ? `- ${user.email}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {!formData.parent_id && (
                                                <p className="text-xs text-gray-500 mt-1">Please select parent first</p>
                                            )}
                                            {formData.parent_id && filteredChildren.length === 0 && (
                                                <p className="text-xs text-red-500 mt-1">No other users available</p>
                                            )}
                                        </div>
                                    </>
                                )}
                                
                                {/* Status Dropdown */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        required
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Enter description or notes about this matching..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center space-x-2"
                                >
                                    {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                                    <span>{editingMatching ? 'Update' : 'Save'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MatchingManagement;